import { mat4, vec3 } from 'gl-matrix';
import { BoundingSphere, ViewFrustum } from '../util';
import { PropertyAnimation } from '../animation';
import { RenderedTexture } from '../texture';
import { RenderedCubeMapTexture } from '../texture/RenderedCubeMapTexture';
import { Context } from '../context/Context';
import { Camera } from '../camera';

export type ShadowMode = 'none' | 'static' | 'dynamic';

export enum LightType {
  Point = 0.0,
  Directional = 1.0,
  Spot = 2.0,
}

export let SHADOW_MAP_DIMENSIONS = { width: 1024, height: 1024 };

export function setPointShadowMapSize(size: number): void {
  SHADOW_MAP_DIMENSIONS = { width: size, height: size };
}

export type BaseLightProperties = {
  reference?: string;
  color?: vec3 | number[];
  specular?: vec3 | number[];
  position: vec3 | number[];
  enabled?: boolean;
  shadowMode?: ShadowMode;
  priority?: number;
};

export abstract class Light {
  public abstract readonly type: LightType;

  public reference: string;
  public animatedProperties?: PropertyAnimation;
  public enabled: boolean;

  public color: vec3 | number[];
  public specular: vec3 | number[];
  public position: vec3 | number[];

  public shadowMap?: RenderedTexture | RenderedCubeMapTexture;
  /** Set by light types that have spatial bounds (point, spot). Undefined for directional. */
  public boundingSphere?: BoundingSphere;
  public camera!: LightCamera;

  // Shadow budget properties
  public shadowMode: ShadowMode;
  public priority: number;
  public shadowDirty: boolean;

  /**
   * Tag set when this Light belongs to a composite (lantern, torch, etc.).
   * Picking and inspector code use this to escalate selection to the
   * composite parent. `undefined` for regular scene-light placements.
   */
  public compositeInstanceId?: string;

  protected constructor(properties: BaseLightProperties) {
    this.reference = properties.reference ?? `light-${Math.floor(Math.random() * 10000)}`;
    this.enabled = properties.enabled ?? true;
    this.color = properties.color === undefined ? vec3.fromValues(1, 1, 1) : properties.color;
    this.specular = properties.specular === undefined ? this.color : properties.specular;
    this.position = properties.position;
    this.shadowMode = properties.shadowMode ?? 'static';
    this.priority = properties.priority ?? 0;
    this.shadowDirty = true;
  }

  public markDirty(): void {
    if (this.shadowMode !== 'none') {
      this.shadowDirty = true;
    }
  }

  /** Update bounds after position changes. No-op for lights without bounds. */
  public abstract updateBounds(): void;

  /** Render this light's shadow map(s). */
  public abstract renderShadowMap(ctx: Context): void;

  /**
   * Pack 16 floats (4 RGBA texels) of GPU light data into the given array
   * starting at `offset`. The byte layout must match `unpackLightData` in
   * deferred-light-fragment.glsl.
   */
  public abstract packGpuData(data: Float32Array, offset: number): void;

  /** Helper used by subclasses to write the shared texel 0 (position + type) and texel 1 (color). */
  protected packCommon(data: Float32Array, o: number): void {
    // Texel 0: position + type
    data[o] = this.position[0] as number;
    data[o + 1] = this.position[1] as number;
    data[o + 2] = this.position[2] as number;
    data[o + 3] = this.type;

    // Texel 1: color
    data[o + 4] = this.color[0] as number;
    data[o + 5] = this.color[1] as number;
    data[o + 6] = this.color[2] as number;
    data[o + 7] = 0;
  }
}

/**
 * Base class for the camera that views the scene from a light's perspective
 * for shadow rendering. Subclasses define the projection and target-position
 * logic specific to their light type.
 */
export abstract class LightCamera<L extends Light = Light> extends Camera {
  public modelViewMatrix!: mat4;
  public projectionMatrix!: mat4;
  public targetPosition!: vec3 | number[];
  public cameraPosition!: vec3 | number[];
  public frustum!: ViewFrustum;
  protected up: vec3 | number[] = vec3.fromValues(0, 1, 0);

  constructor(
    dimensions: { width: number; height: number },
    public light: L,
  ) {
    super(dimensions);
    this.name = `Light ${light.position}`;
  }

  public setup(): void {
    this.cameraPosition = vec3.copy(vec3.create(), this.light.position);
    this.targetPosition = this.determineTargetPosition();
    this.up = this.determineUp();
    this.modelViewMatrix = this.determineModelView();
    this.projectionMatrix = this.determineProjection();
    this.frustum = this.determineFrustum();
  }

  protected abstract determineTargetPosition(): vec3;
  protected abstract determineProjection(): mat4;
  protected abstract determineUp(): vec3 | number[];

  protected determineModelView(): mat4 {
    return mat4.lookAt(mat4.create(), this.cameraPosition, this.targetPosition, this.up);
  }

  protected determineFrustum(): ViewFrustum {
    let invertedMatrix = mat4.create();
    mat4.multiply(invertedMatrix, this.projectionMatrix, this.modelViewMatrix);
    mat4.invert(invertedMatrix, invertedMatrix);
    return new ViewFrustum(invertedMatrix);
  }

  /**
   * Compute an up vector that is not parallel to the view direction.
   * Used by directional and spot light cameras.
   */
  protected computeNonParallelUp(): vec3 {
    const viewDir = vec3.sub(
      vec3.create(),
      this.targetPosition as vec3,
      this.cameraPosition as vec3,
    );
    vec3.normalize(viewDir, viewDir);
    let candidateUp = vec3.fromValues(0, 1, 0);
    if (Math.abs(vec3.dot(viewDir, candidateUp)) > 0.95) {
      candidateUp = vec3.fromValues(0, 0, 1);
    }
    return candidateUp;
  }

  public prepareFrame(): void {}
}
